import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { History, TrendingUp, TrendingDown, Package, ShoppingCart, Edit } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InventoryHistory = () => {
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    movement_type: 'all',
    start_date: '',
    end_date: '',
    limit: 100
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMovements();
    loadStats();
  }, []);

  const loadMovements = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.movement_type && filters.movement_type !== 'all') params.append('movement_type', filters.movement_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', filters.limit);

      const response = await axios.get(`${API}/inventory-movements?${params.toString()}`);
      setMovements(response.data);
    } catch (error) {
      toast.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/inventory-movements/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas');
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'venta': return <ShoppingCart className="w-4 h-4" />;
      case 'reabastecimiento': return <TrendingUp className="w-4 h-4" />;
      case 'ajuste': return <Edit className="w-4 h-4" />;
      case 'devolucion': return <TrendingDown className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getMovementBadge = (type, quantity) => {
    const configs = {
      venta: { label: 'Venta', className: 'bg-rose-100 text-rose-800' },
      reabastecimiento: { label: 'Reabastecimiento', className: 'bg-green-100 text-green-800' },
      ajuste: { label: 'Ajuste', className: 'bg-blue-100 text-blue-800' },
      devolucion: { label: 'Devolución', className: 'bg-amber-100 text-amber-800' }
    };
    const config = configs[type] || configs.ajuste;
    
    return (
      <Badge className={config.className}>
        {getMovementIcon(type)}
        <span className="ml-1">{config.label}</span>
      </Badge>
    );
  };

  const applyFilters = () => {
    setLoading(true);
    loadMovements();
  };

  const resetFilters = () => {
    setFilters({
      movement_type: 'all',
      start_date: '',
      end_date: '',
      limit: 100
    });
    setTimeout(() => loadMovements(), 100);
  };

  if (loading && movements.length === 0) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="inventory-history-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Historial de Inventario</h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Movimientos Hoy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold mono-font">{stats.today_movements}</p>
            </CardContent>
          </Card>

          {stats.by_type && Object.entries(stats.by_type).map(([type, data]) => (
            <Card key={type} className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-600 capitalize flex items-center">
                  {getMovementIcon(type)}
                  <span className="ml-2">{type}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold mono-font">{data.quantity}</p>
                <p className="text-xs text-slate-500">{data.count} movimientos</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Movimiento</Label>
              <Select
                value={filters.movement_type || "all"}
                onValueChange={(value) => setFilters({ ...filters, movement_type: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-movement-type">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="venta">Venta</SelectItem>
                  <SelectItem value="reabastecimiento">Reabastecimiento</SelectItem>
                  <SelectItem value="ajuste">Ajuste</SelectItem>
                  <SelectItem value="devolucion">Devolución</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="filter-start-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="filter-end-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Límite</Label>
              <Select
                value={filters.limit.toString()}
                onValueChange={(value) => setFilters({ ...filters, limit: parseInt(value) })}
              >
                <SelectTrigger data-testid="filter-limit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={applyFilters}
                className="flex-1 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md font-medium"
                data-testid="apply-filters-button"
              >
                Aplicar
              </button>
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md font-medium"
                data-testid="reset-filters-button"
              >
                Limpiar
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Movements List */}
      <div className="space-y-3" data-testid="movements-list">
        {movements.map((movement, index) => (
          <Card key={movement.id || index} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`movement-${index}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getMovementBadge(movement.movement_type, movement.quantity)}
                    <span className="text-sm text-slate-500">
                      {new Date(movement.created_at).toLocaleString('es-DO', {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      })}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-slate-600">Producto</p>
                      <p className="font-semibold">{movement.product_name}</p>
                      <p className="text-sm text-slate-500">{movement.size} - {movement.color}</p>
                    </div>

                    <div>
                      <p className="text-xs text-slate-600">Cantidad</p>
                      <p className={`text-2xl font-bold mono-font ${
                        movement.quantity > 0 ? 'text-green-600' : 'text-rose-600'
                      }`}>
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-slate-600">Stock Anterior → Nuevo</p>
                      <p className="font-bold mono-font">
                        {movement.stock_before} → {movement.stock_after}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-slate-600">Usuario</p>
                      <p className="text-sm">{movement.created_by}</p>
                      {movement.notes && (
                        <p className="text-xs text-slate-500 mt-1">{movement.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {movements.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No hay movimientos registrados con estos filtros
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryHistory;
